import { Action } from "@/api-schema-enums";
import { useCasaviContactWhitelistCreateMutation } from "@/common/api/casavi/contact-whitelist/use-casavi-contact-whitelist-create.mutation";
import { useCasaviContactWhitelistUpdateMutation } from "@/common/api/casavi/contact-whitelist/use-casavi-contact-whitelist-update.mutation";
import { handleFormSubmission } from "@/common/helpers/form-submission";
import { Form } from "@/components/ui/form";
import { getCasaviFilterFormValues } from "@/modules/casavi/_helpers/casavi-filter-form-values";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import {
  CasaviContactWhitelist,
  CasaviContactWhitelistFormSchema,
  CasaviContactWhitelistFormSchemaType,
} from "../_helpers/validate-casavi-contact-whitelist";
import { ContactWhitelistFormFields } from "./contact-whitelist-form-fields";

interface ContactWhitelistFormProps {
  mode: Action.Create | Action.Update;
  uuid?: string;
  initialData?: CasaviContactWhitelist;
}

export function ContactWhitelistForm({ mode, uuid, initialData }: ContactWhitelistFormProps) {
  const { t } = useTranslation(["casavi"]);
  const [_, navigate] = useLocation();
  const initialFormValues = useMemo(() => getCasaviFilterFormValues(initialData), [initialData]);

  const createMutation = useCasaviContactWhitelistCreateMutation();
  const updateMutation = useCasaviContactWhitelistUpdateMutation();

  const isPending = mode === Action.Create ? createMutation.isPending : updateMutation.isPending;

  const form = useForm<CasaviContactWhitelistFormSchemaType>({
    resolver: zodResolver(CasaviContactWhitelistFormSchema),
    mode: "onChange",
    defaultValues: initialFormValues,
  });

  useEffect(() => {
    if (initialData) {
      form.reset(initialFormValues, {
        keepDefaultValues: false,
      });
    }
  }, [form, initialData, initialFormValues]);

  const onSubmit = async (values: CasaviContactWhitelistFormSchemaType): Promise<void> => {
    const messages =
      mode === Action.Create
        ? {
            success: t("casavi:ContactWhiteListSuccessCreate"),
            error: t("casavi:ContactWhiteListErrorCreate"),
          }
        : {
            success: t("casavi:ContactWhiteListSuccessUpdate"),
            error: t("casavi:ContactWhiteListErrorUpdate"),
          };

    if (mode === Action.Create) {
      await handleFormSubmission(
        () => createMutation.mutateAsync(values),
        messages,
        () => navigate("~/cas/contact-whitelist"),
      );
    } else {
      if (!uuid) return;

      await handleFormSubmission(
        () =>
          updateMutation.mutateAsync({
            uuid,
            ...values,
          }),
        messages,
        () => navigate("~/cas/contact-whitelist"),
      );
    }
  };

  return (
    <Form {...form}>
      <ContactWhitelistFormFields
        form={form}
        onSubmit={onSubmit}
        isPending={isPending}
        mode={mode}
        currentUuid={uuid}
      />
    </Form>
  );
}
